import { DialogM } from "@common/components/alert/dialog/Dialog"
import FilledButtonM from "@common/components/button/FilledButtonM"
import TextM from "@common/components/text/TextM"
import TitleM from "@common/components/text/TitleM"
import { useApiStatus } from "@common/hooks/useApiStatus"
import useLiveKitEventSender from "@common/hooks/livekit/useLiveKitEventSender"
import { theme } from "@common/theme/theme"
import { DimensionsContext } from "@common/utils/mobileUtils"
import { apiIdle, apiRequest } from "@data/redux/actions/api"
import { activitiesService } from "@data/services/activitiesService"
import { authService } from "@data/services/authService"
import { studentsService } from "@data/services/studentsService"
import { hasRole } from "@data/utility/ability"
import { findItemInListWithId } from "@data/utility/dataUtils"
import { useNavigation } from "@react-navigation/native"
import { StatusBar } from "@common/systemBars/StatusBar"
import React, { useContext, useEffect, useState } from "react"
import {
    Dimensions,
    FlatList,
    Platform,
    RefreshControl,
    TouchableOpacity,
    View,
} from "react-native"
import { ActivityIndicator } from "react-native-paper"
import FontAwesome5 from "react-native-vector-icons/FontAwesome5"
import { connect } from "react-redux"
import IconButtonM from "@common/components/button/IconButtonM"
import { IconImageM } from "@common/components/icons/IconsM"
import RealizationsListItem from "../../organize/list/RealizationsListItem"
import TodoModal from "./TodoModal"
import StudentMainMenuHeader from "@common/header/StudentMainMenuHeader"
import { useSafeAreaInsets } from "react-native-safe-area-context"
import { PaginatedFlatList } from "@common/screens/PaginatedFlatList"
import { parentOnlyBridge } from "../../../featureFlags/screenBridge"
import { ArchivedActivitiesScreen as PortsArchivedActivitiesScreen } from "../../../ports/primary/ui/screens/parent/Activities/ArchivedActivitiesScreen"

const LegacyArchivedActivitiesScreen = (props) => {
    const navigation = useNavigation()
    const dimensions = useContext(DimensionsContext)
    const route = props.route
    const insets = useSafeAreaInsets()

    const [archivedActivities, setArchivedActivities] = useState([])
    const [selectionMode, setSelectionMode] = useState(false)
    const [selectedActivities, setSelectedActivities] = useState([])
    const [isLoading, setIsLoading] = useState(false)
    const [isTodoOpen, setIsTodoOpen] = useState(false)
    const [selectedItem, setSelectedItem] = useState(null)
    const PER_PAGE = 20
    const studentId = route?.params?.studentId ?? route?.params?.student?.id ?? props.user?.id
    const isParentMode = props.isParentMode

    const { sendLiveKitData: updateTodo } = useLiveKitEventSender()

    const schoolSubject = findItemInListWithId(
        selectedItem?.school_subject_id,
        props.schoolSubjects
    )

    // Update selectedItem when archivedActivities changes (to reflect fresh data with logs)
    useEffect(() => {
        if (selectedItem?.id) {
            const updatedItem = archivedActivities.find(
                (activity) => activity?.id === selectedItem?.id
            )
            if (updatedItem) {
                setSelectedItem(updatedItem)
            }
        }
    }, [archivedActivities])

    // Fetch archived activities on mount
    useEffect(() => {
        setArchivedActivities([])
    }, [studentId])
    useApiStatus(
        activitiesService.getArchivedActivities,
        null,
        true,
        (data, allData, urlParams) => {
            // Backend shape: { activities: [...], challenges: [...], meta: { total, last_page, current_page, per_page } }
            const receivedPage = urlParams?.page ?? 1
            const isAppend = receivedPage > 1
            const list = data?.activities ?? []
            const meta = data?.meta

            setArchivedActivities((prev) => (isAppend ? [...prev, ...list] : list))

            setIsLoading(false)
        },
        () => {
            setIsLoading(false)
        },
        true
    )

    useApiStatus(
        activitiesService.restoreArchivedActivities,
        "Activités restaurées avec succès !",
        true,
        (data) => {
            // Send LiveKit event to update todo list
            const restoredActivities = data?.activities || []
            if (restoredActivities.length > 0) {
                restoredActivities.forEach((activity) => {
                    updateTodo(
                        "newTodo",
                        {
                            id: studentId,
                            activity: activity,
                        },
                        [studentId]
                    )
                })
            }

            // Refresh archived activities list (page 1)
            setSelectedActivities([])
            setSelectionMode(false)
            setArchivedActivities([])
            props.apiRequest(
                activitiesService.getArchivedActivities,
                { student: studentId },
                {},
                { page: 1, per_page: PER_PAGE }
            )

            // Refresh student data to update todo list for tutors
            if (hasRole(props.user, "tutor") && studentId) {
                props.apiRequest(studentsService.getStudent, { id: studentId })
            }
        }
    )

    // Listen to getActivities success to update archivedActivities with fresh data (with logs)
    useApiStatus(
        activitiesService.getActivities,
        null,
        true,
        (data) => {
            // Update archivedActivities with fresh data from getActivities (called from TodoModal refresh)
            if (data && data.length > 0) {
                setArchivedActivities((prevActivities) =>
                    prevActivities.map((activity) => {
                        const updatedActivity = data.find((newActivity) => newActivity?.id === activity?.id)
                        return updatedActivity || activity
                    })
                )
            }
        }
    )

    const toggleSelectionMode = () => {
        setSelectionMode(!selectionMode)
        setSelectedActivities([])
    }

    const handleActivitySelection = (activityId) => {
        if (selectedActivities.includes(activityId)) {
            setSelectedActivities(
                selectedActivities.filter((id) => id !== activityId)
            )
        } else {
            setSelectedActivities([...selectedActivities, activityId])
        }
    }

    const handleRestoreMultiple = () => {
        if (selectedActivities.length === 0) return

        DialogM.show({
            champion: "zoe",
            text1: `Restaurer ${selectedActivities.length} activité${
                selectedActivities.length > 1 ? "s" : ""
            } dans la todo-list ?`,
            text2: props.isParentVoice
                ? "Ces activités seront ajoutées à la liste d'activités à faire."
                : "Ces activités seront ajoutées à ta liste d'activités à faire.",
            buttonText1: "Restaurer",
            buttonColor1: theme.colors.primary,
            onPress: () => {
                props.apiRequest(
                    activitiesService.restoreArchivedActivities,
                    { student: studentId },
                    { activity_ids: selectedActivities }
                )
            },
            buttonText2: "Annuler",
        })
    }

    const renderItem = ({ item: activity }) => {
        const isSelected = selectedActivities.includes(activity?.id)

        return (
            <RealizationsListItem
                activity={activity}
                custom
                todo
                noOptions
                student={route?.params?.student}
                style={{
                    opacity: 0.8,
                    width: dimensions?.width - 40,
                    marginTop: 7,
                }}
                selected={isSelected}
                selectionMode={selectionMode}
                onPress={() => {
                    if (selectionMode) {
                        handleActivitySelection(activity?.id)
                    } else {
                        setSelectedItem(activity)
                        setIsTodoOpen(true)
                    }
                }}
                onLongPress={() => {
                    if (!selectionMode) {
                        setSelectionMode(true)
                        handleActivitySelection(activity?.id)
                    }
                }}
            />
        )
    }


    const renderEmpty = () => (
        <View
            style={{
                flex: 1,
                justifyContent: "center",
                alignItems: "center",
                paddingVertical: 50,
            }}
        >
            <FontAwesome5
                name="archive"
                size={60}
                color={theme.colors.lightMore}
            />
            <TextM
                fontSize={16}
                fontWeight="SemiBold"
                color={theme.colors.light}
                style={{ marginTop: 20 }}
            >
                Aucune activité archivée
            </TextM>
        </View>
    )


    return (
        <View
            style={{
                flex: 1,
            }}
        >
            <View
                style={{
                    paddingVertical: 5,
                    paddingTop: insets.top + 5,
                    backgroundColor: theme.colors.white,
                    paddingRight: 10,
                    borderBottomWidth: 1,
                    borderColor: theme.colors.lightMore,
                }}
            >
                {(hasRole(props.user, "tutor") || isParentMode) ? (
                    <StudentMainMenuHeader studentId={studentId} withGoBack />
                ) : (
                    <View
                        style={{
                            flexDirection: "row",
                            alignItems: "center",
                            paddingHorizontal: 10,
                        }}
                    >
                        <IconButtonM
                            icon="arrow-left"
                            iconColor={theme.colors.primary}
                            size={32}
                            onPress={() => {
                                if (navigation.canGoBack()) navigation.goBack()
                                else navigation.navigate("menu")
                            }}
                        />
                    </View>
                )}
            </View>


            <View
                style={{
                    flex: 1,
                    alignItems: "center",
                    width:
                        Platform.OS === "web"
                            ? Dimensions.get("window").width
                            : null,
                    alignSelf: "center",
                }}
            >
                <TitleM
                    style={{ marginTop: 10 }}
                    fontWeight="ExtraBold"
                    wrap
                >
                    ACTIVITÉS ARCHIVÉES
                </TitleM>

                <View style={{ width: "100%", flex: 1 }}>
                    {props.getArchivedActivitiesRequest &&<ActivityIndicator animating={true}
                                                                             style={{
                                                                                 marginTop: 50
                                                                             }}
                                                                             size="large"
                                                                             color={theme.colors.primary}/>}
                    <PaginatedFlatList
                        data={archivedActivities}
                        requestApiService={activitiesService.getArchivedActivities}
                        requestRouteParams={{ student: studentId }}
                        requestUrlParams={{ per_page: PER_PAGE, with_logs: 1 }}
                        noActivityIndicator={true}
                        style={{
                            width:
                                Platform.OS !== "web"
                                    ? dimensions?.width
                                    : "100%",
                        }}
                        ListHeaderComponent={
                            archivedActivities.length > 0 ? (
                                <View
                                    style={{
                                        alignSelf: "center",
                                        width:
                                            (dimensions?.width || Dimensions.get("window").width) - 40,
                                    }}
                                >
                                    <View
                                        style={{
                                            flexDirection: "row",
                                            alignItems: "center",
                                            justifyContent: "space-between",
                                            width: "100%",
                                            marginVertical: 15,
                                        }}
                                    >
                                        {selectionMode ? (
                                            <TouchableOpacity
                                                onPress={toggleSelectionMode}
                                                style={{
                                                    flexDirection: "row",
                                                    alignItems: "center",
                                                }}
                                            >
                                                <FontAwesome5
                                                    name="times"
                                                    size={16}
                                                    color={theme.colors.primary}
                                                    style={{ marginRight: 8 }}
                                                />
                                                <TextM
                                                    fontSize={12}
                                                    color={theme.colors.primary}
                                                    fontWeight="Bold"
                                                >
                                                    {selectedActivities.length} élément{selectedActivities.length > 1 ? 's' : ''} sélectionné{selectedActivities.length > 1 ? 's' : ''}
                                                </TextM>
                                            </TouchableOpacity>
                                        ) : (
                                            <View />
                                        )}

                                        <TouchableOpacity
                                            onPress={() => {
                                                if (!selectionMode) {
                                                    setSelectionMode(true)
                                                    const allActivityIds =
                                                        archivedActivities?.map((activity) => activity?.id) || []
                                                    setSelectedActivities(allActivityIds)
                                                } else {
                                                    if (
                                                        selectedActivities.length === archivedActivities?.length
                                                    ) {
                                                        setSelectedActivities([])
                                                    } else {
                                                        const allActivityIds =
                                                            archivedActivities?.map((activity) => activity?.id) || []
                                                        setSelectedActivities(allActivityIds)
                                                    }
                                                }
                                            }}
                                            style={{
                                                flexDirection: "row",
                                                alignItems: "center",
                                            }}
                                        >
                                            <TextM style={{ marginRight: 10 }}>
                                                Sélectionner tout
                                            </TextM>

                                            <View
                                                style={{
                                                    width: 20,
                                                    height: 20,
                                                    borderRadius: 4,
                                                    backgroundColor:
                                                        selectionMode &&
                                                        archivedActivities.length > 0 &&
                                                        selectedActivities.length === archivedActivities.length
                                                            ? theme.colors.primary
                                                            : theme.colors.white,
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                    elevation: 5,
                                                    borderWidth:
                                                        selectionMode &&
                                                        archivedActivities.length > 0 &&
                                                        selectedActivities.length === archivedActivities.length
                                                            ? 0
                                                            : 2,
                                                    borderColor: theme.colors.lightMore,
                                                }}
                                            >
                                                {selectionMode &&
                                                    archivedActivities.length > 0 &&
                                                    selectedActivities.length === archivedActivities.length && (
                                                        <FontAwesome5
                                                            name="check"
                                                            size={10}
                                                            color={theme.colors.white}
                                                        />
                                                    )}
                                            </View>
                                        </TouchableOpacity>
                                    </View>
                                </View>
                            ) : (
                                <></>
                            )
                        }
                        renderItem={renderItem}
                        contentContainerStyle={{
                            alignItems: "center",
                            paddingBottom: selectionMode ? 150 : 20,
                        }}
                        ListEmptyComponent={(archivedActivities.length ===0 && !props.getArchivedActivitiesRequest) && renderEmpty()}
                    />

                    {!!selectedItem && (
                        <TodoModal
                            isOpen={isTodoOpen}
                            handleShowModal={() => setIsTodoOpen(false)}
                            item={selectedItem}
                            studentId={studentId}
                            student={route?.params?.student ?? props.user}
                            navigation={navigation}
                            route={route}
                            fromArchived={true}
                            schoolSubject={schoolSubject}
                            onExit={() => setIsTodoOpen(false)}
                        />
                    )}
                </View>


                {!selectionMode && archivedActivities.length > 0 && (
                    <View
                        style={{
                            position: "absolute",
                            bottom: 20,
                            right: 20,
                        }}
                    >
                        <TouchableOpacity
                            onPress={toggleSelectionMode}
                            style={{
                                backgroundColor: theme.colors.primary,
                                width: 60,
                                height: 60,
                                borderRadius: 30,
                                justifyContent: "center",
                                alignItems: "center",
                                shadowColor: "#000",
                                shadowOffset: { width: 0, height: 2 },
                                shadowOpacity: 0.25,
                                shadowRadius: 3.84,
                                elevation: 5,
                            }}
                        >
                            <FontAwesome5
                                name="undo"
                                size={24}
                                color={theme.colors.white}
                            />
                        </TouchableOpacity>
                    </View>
                )}

                {selectionMode && archivedActivities.length > 0 && (
                    <View
                        style={{
                            position: "absolute",
                            bottom: 0,
                            left: 0,
                            right: 0,
                            backgroundColor: theme.colors.white,
                            paddingVertical: 8,
                            paddingHorizontal: 20,
                            alignItems: "center",
                        }}
                    >
                        <FilledButtonM
                            style={{
                                maxWidth: 400,
                                alignSelf: 'center',
                            }}
                            color={theme.colors.primary}
                            textColor={theme.colors.white}
                            onPress={handleRestoreMultiple}
                            disabled={
                                selectedActivities.length === 0 ||
                                props.restoreArchivedActivitiesRequest
                            }
                            loading={props.restoreArchivedActivitiesRequest}
                            icon="undo"
                            label={`Restaurer (${selectedActivities.length})`}
                        />
                    </View>
                )}
            </View>

        </View>
    )
}

const mapStateToProps = (state) => {
    return {
        user: state.data.currentUser?.object,
        activityTemplates: state.data.staticData?.activity_templates,
        schoolSubjects: state.data.staticData?.school_subjects,
        getArchivedActivitiesRequest:
        state.api.getArchivedActivities?.request,
        restoreArchivedActivitiesRequest:
        state.api.restoreArchivedActivities?.request,
        isParentMode: state.data.isParentMode,
        // `isParentMode` ci-dessus lit un chemin d'état qui n'existe pas — le
        // drapeau vit sous `data.family`. On ne le corrige pas ici, ça
        // changerait le header de l'écran ; mais la voix des textes a besoin
        // de la vraie valeur.
        isParentVoice: state.data.family?.isParentMode,
    }
}

const mapDispatchToProps = {
    apiRequest,
    apiIdle,
}

const ConnectedLegacyArchivedActivitiesScreen = connect(
    mapStateToProps,
    mapDispatchToProps
)(LegacyArchivedActivitiesScreen)

/**
 * « Activités archivées » — pont **mode parent seulement** : l'écran sert aussi
 * l'élève et le tuteur. Le chemin du design system monte lui-même la fiche d'une activité
 * (`useActivityDetailsFlow`, entrée « archives »).
 */
export default parentOnlyBridge(
    PortsArchivedActivitiesScreen,
    ConnectedLegacyArchivedActivitiesScreen
)
